import { useEffect, useState } from 'react';
import useUserDetails from '../hooks/useUserDetails';
import { isImageTag, resolveImageTag } from '../../services/visualizationService';

interface ChatVizImageProps {
  src?: string;
  alt?: string;
}

// Browsers can't decode HEIC/HEIF (the default iPhone photo format), so an <img>
// would render a broken-image icon. The presigned URL carries the original filename
// (…filename="IMG_1234.HEIC"…), so detect it there and offer a download link instead.
const HEIC_URL_REGEX = /\.hei[cf]\b/i;

const decodeSrc = (src: string): string => {
  try {
    return decodeURIComponent(src);
  } catch {
    return src;
  }
};

const isHeicUrl = (src: string): boolean => HEIC_URL_REGEX.test(decodeSrc(src));

const getFileName = (src: string): string | null => {
  const match = decodeSrc(src).match(/filename="?([^";]+)"?/i);
  return match ? match[1] : null;
};

const PlainImage = ({ src, alt }: { src: string; alt?: string }) => {
  if (isHeicUrl(src)) {
    const label = alt || getFileName(src) || 'image';
    return (
      <a className="chat-viz-image-download" href={src} target="_blank" rel="noreferrer">
        {label} ↗
      </a>
    );
  }
  return (
    <a className="chat-viz-image-link" href={src} target="_blank" rel="noreferrer">
      <img className="chat-viz-image" src={src} alt={alt ?? ''} />
    </a>
  );
};

// Presigned URLs returned by the backend expire (~30 min), so chat stores stable
// xqviz:// / xqsurviz:// tags instead. Resolve each tag to a fresh URL at render time and cache
// it with a TTL well under the presigned lifetime so remounts never reuse an expired URL.
const RESOLVED_TAG_TTL_MS = 10 * 60 * 1000;
const resolvedTagCache = new Map<string, { url: string; resolvedAt: number }>();

const getCachedUrl = (tag: string): string | null => {
  const entry = resolvedTagCache.get(tag);
  if (!entry) return null;
  if (Date.now() - entry.resolvedAt > RESOLVED_TAG_TTL_MS) {
    resolvedTagCache.delete(tag);
    return null;
  }
  return entry.url;
};

const TaggedChatVizImage = ({ tag, alt }: { tag: string; alt?: string }) => {
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const [url, setUrl] = useState<string | null>(() => getCachedUrl(tag));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (url) return;
    const accessToken = userDetails?.access_token;
    if (!accessToken) return;

    let cancelled = false;
    void (async () => {
      try {
        const resolved = await resolveImageTag(tag, accessToken);
        if (cancelled) return;
        if (!resolved) {
          setFailed(true);
          return;
        }
        resolvedTagCache.set(tag, { url: resolved, resolvedAt: Date.now() });
        setUrl(resolved);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tag, url, userDetails?.access_token]);

  if (failed) {
    return (
      <span className="chat-viz-image-message">
        {alt ? `Could not load “${alt}”.` : 'Could not load visualization.'}
      </span>
    );
  }
  if (!url) {
    return <span className="chat-viz-image-loading">{alt ? `Loading “${alt}”…` : 'Loading visualization…'}</span>;
  }
  return <PlainImage src={url} alt={alt} />;
};

const ChatVizImage = ({ src, alt }: ChatVizImageProps) => {
  if (!src) return null;
  if (isImageTag(src)) return <TaggedChatVizImage tag={src} alt={alt} />;
  return <PlainImage src={src} alt={alt} />;
};

export default ChatVizImage;
